// src/components/aiondemand/AIOnDemandPanel.jsx
//
// AI on Demand workspace panel.
//
// Mounted by MapPage in the left panel slot when activeProduct === "helios".
// (The route/tab key is still "helios" for now — rename is a separate pass.)
//

import { useMemo } from "react";
import {
    Sparkle,
    MapPin,
    Eye,
    BrainCircuit,
    Activity,
    History,
} from "lucide-react";
import * as turf from "@turf/turf";

import { useAoiStore } from "../../stores/useAoiStore";
import { useProjectStore } from "../../stores/useProjectStore";
import { useAIOnDemandStore } from "../../stores/useAIOnDemandStore";
import { useScope } from "../../actions/useScope.js";

import ActionPreview from "./ActionPreview.jsx";

import { ACTIONS } from "../../actions/registry.js";
import ActionsList from "./ActionsList.jsx";

import JobsSection from './JobsSection.jsx';
import JobDetail from './JobDetail.jsx';
import { useJobPolling } from './useJobPolling.js';
import ArchivedJobsSection from './ArchivedJobsSection.jsx';

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Crosshair } from "lucide-react";

/* ── Helpers ─────────────────────────────────────────────────────────── */

function aoiAreaKm2(geom) {
    if (!geom) return null;
    try {
        const m2 = turf.area({ type: "Feature", geometry: geom, properties: {} });
        return m2 / 1_000_000;
    } catch {
        return null;
    }
}

function formatKm2(km2) {
    if (km2 == null) return null;
    if (km2 < 0.01) return "<0.01 km²";
    if (km2 < 1) return `${km2.toFixed(2)} km²`;
    if (km2 < 100) return `${km2.toFixed(1)} km²`;
    return `${Math.round(km2)} km²`;
}

/* ── Main panel ──────────────────────────────────────────────────────── */

export default function AIOnDemandPanel({ mapRef }) {
    const aois = useAoiStore((s) => s.aois);
    const activeAoiId = useAoiStore((s) => s.activeAoiId);

    const activeProject = useProjectStore((s) => s.activeProject);
    const activeProjectId = useProjectStore((s) => s.activeProjectId);
    const isGlobal = useProjectStore((s) => s.isGlobal);

    const clearSelection = useAIOnDemandStore((s) => s.clearSelection);

    const { getScope } = useScope(mapRef);

    const activeAoi = useMemo(
        () => (aois ?? []).find((a) => a.id === activeAoiId) ?? null,
        [aois, activeAoiId]
    );

    const aoiArea = useMemo(
        () => aoiAreaKm2(activeAoi?.geom),
        [activeAoi?.geom]
    );

    const selectedActionId = useAIOnDemandStore((s) => s.selectedActionId);
    const selectedJobId = useAIOnDemandStore((s) => s.selectedJobId);
    const selectActionRaw = useAIOnDemandStore((s) => s.selectAction);
    const clearActionSelection = useAIOnDemandStore((s) => s.clearActionSelection);
    const selectJob = useAIOnDemandStore((s) => s.selectJob);
    const clearJobSelection = useAIOnDemandStore((s) => s.clearJobSelection);

    const selectAction = (actionId) => {
        const action = ACTIONS.find((a) => a.id === actionId);
        selectActionRaw(actionId, action?.preview?.layers ?? []);
    };

    useJobPolling();

    if (isGlobal || !activeProjectId) {
        return (
            <div className="flex flex-col h-full">
                <PanelHeader />
                <div className="flex-1 flex items-center justify-center px-4">
                    <p className="text-xs text-fg-secondary text-center leading-relaxed">
                        Select a project to run<br />AI actions on demand.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <PanelHeader projectName={activeProject?.name} />

            <ScopePicker
                aois={aois}
                activeProjectId={activeProjectId}
                activeAoi={activeAoi}
                aoiArea={aoiArea}
                mapRef={mapRef}
            />

            {/* Body — either the models list with sections, or a selected model's preview */}
            {selectedActionId ? (
                <ActionPreview
                    actionId={selectedActionId}
                    mapRef={mapRef}
                    getScope={getScope}
                    onBack={clearActionSelection}
                    onSelectJob={selectJob}
                />
            ) : selectedJobId ? (
                <JobDetail
                    jobId={selectedJobId}
                    mapRef={mapRef}
                    onBack={clearJobSelection}
                />
            ) : (
                <div className="flex-1 overflow-y-auto">
                    <ActionsList onSelect={selectAction} />
                    <JobsSection onSelectJob={selectJob} />
                    <ArchivedJobsSection onSelectJob={selectJob} />
                </div>
            )}

            {/* Footer — hidden when a model is open so the preview owns the action bar */}
            {!selectedActionId && (
                <div className="px-3 py-2 border-t border-slate-800 shrink-0">
                    <p className="text-[10px] text-fg-secondary leading-relaxed">
                        AI on Demand runs AI actions on your AOI or current viewport.
                        Pin an AOI in the{" "}
                        <span className="text-fg-secondary">Areas of Interest</span> panel
                        for scoped analysis.
                    </p>
                </div>
            )}
        </div>
    );
}

/* ── Panel header ────────────────────────────────────────────────────── */

function PanelHeader({ projectName }) {
    return (
        <div className="p-3 border-b border-slate-800 flex items-center justify-between shrink-0">
            <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Sparkle size={12} />
                    AI on Demand
                </div>
                {projectName && (
                    <p className="text-[10px] text-fg-secondary mt-0.5 truncate max-w-[200px]">
                        {projectName}
                    </p>
                )}
            </div>
        </div>
    );
}

/* ── Context bar ─────────────────────────────────────────────────────── */

function ContextBar({ activeAoi, aoiArea }) {
    const hasAoi = Boolean(activeAoi);
    const hasGeom = Boolean(activeAoi?.geom);

    return (
        <div className="px-3 py-2.5 border-b border-slate-800 shrink-0 bg-bg-soft/40">
            <div className="text-[9px] font-semibold tracking-[0.14em] font-mono uppercase text-fg-secondary mb-1.5">
                Scope
            </div>

            {hasAoi ? (
                <ScopeChip
                    icon={<MapPin size={11} />}
                    label={activeAoi.name}
                    meta={hasGeom ? formatKm2(aoiArea) : "no boundary drawn"}
                    dotColor={
                        hasGeom
                            ? "bg-accent-info shadow-[0_0_4px_#3AAEE8]"
                            : "bg-slate-700"
                    }
                    tone="aoi"
                />
            ) : (
                <>
                    <ScopeChip
                        icon={<Eye size={11} />}
                        label="Viewport"
                        meta="current map view"
                        dotColor="bg-slate-600"
                        tone="viewport"
                    />
                    <p className="mt-1.5 text-[10px] text-fg-secondary leading-relaxed">
                        No AOI pinned — actions will use the current map viewport.
                    </p>
                </>
            )}
        </div>
    );
}


/* ── Sections ────────────────────────────────────────────────────────── */

function ActionSection({ onSelect }) {
    return (
        <div className="border-b border-slate-800">
            <div className="px-3 py-2 flex items-center gap-1.5">
                <span className="text-fg-secondary"><BrainCircuit size={11} /></span>
                <h3 className="text-[10px] font-semibold tracking-[0.14em] font-mono uppercase text-slate-400">
                    Actions
                </h3>
            </div>
            <div className="pb-3">
                <ActionsList onSelect={onSelect} />
            </div>
        </div>
    );
}

function PlaceholderSection({ icon, title, empty }) {
    return (
        <div className="border-b border-slate-800 last:border-b-0">
            <div className="px-3 py-2 flex items-center gap-1.5">
                <span className="text-fg-secondary">{icon}</span>
                <h3 className="text-[10px] font-semibold tracking-[0.14em] font-mono uppercase text-slate-400">
                    {title}
                </h3>
            </div>
            <div className="px-3 pb-3">
                <p className="text-[10px] text-fg-secondary italic leading-relaxed">
                    {empty}
                </p>
            </div>
        </div>
    );
}

function ScopePicker({ aois, activeProjectId, activeAoi, aoiArea, mapRef }) {

    /*
    console.log('[ScopePicker DEBUG]', {
        activeProjectId,
        aois: aois?.map(a => ({
            name: a.name,
            project_id: a.project_id,
            project_match: a.project_id === activeProjectId,
            hasGeom: !!a.geom,
            passesFilter: a.project_id === activeProjectId && !!a.geom
        }))
    });
    */
   
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const setActiveAoi = useAoiStore((s) => s.setActiveAoi);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handle = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [open]);

    const projectAois = (aois ?? []).filter(
        (a) => a.project_id === activeProjectId && a.geom
    );

    function pickViewport() {
        setActiveAoi(null);
        setOpen(false);
    }

    function pickAoi(aoiId) {
        setActiveAoi(aoiId);
        setOpen(false);
    }

    function flyToAoi() {
        const map = mapRef?.current;
        if (!map || !activeAoi?.geom) return;
        try {
            const bbox = turf.bbox({ type: "Feature", geometry: activeAoi.geom });
            map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
                padding: 60,
                duration: 800,
            });
        } catch (err) {
            console.warn("[ScopePicker] flyTo failed", err);
        }
    }

    const hasAoi = Boolean(activeAoi);

    return (
        <div className="px-3 py-2.5 border-b border-slate-800 shrink-0 bg-bg-soft/40 relative" ref={ref}>
            <div className="text-[9px] font-semibold tracking-[0.14em] font-mono uppercase text-fg-secondary mb-1.5">
                Scope
            </div>

            <button
                onClick={() => setOpen((v) => !v)}
                className={[
                    "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs transition",
                    hasAoi
                        ? "bg-accent-action/10 border-accent-action/40 text-accent-action hover:bg-accent-action/15"
                        : "bg-bg border-slate-700 text-slate-300 hover:border-slate-600",
                ].join(" ")}
            >
                <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${hasAoi
                        ? "bg-accent-info shadow-[0_0_4px_#16f2d4]"
                        : "bg-slate-600"
                        }`}
                />
                <span className="shrink-0 opacity-80">
                    {hasAoi ? <MapPin size={11} /> : <Eye size={11} />}
                </span>
                <span className="flex-1 truncate font-medium text-left">
                    {hasAoi ? activeAoi.name : "Viewport"}
                </span>
                {hasAoi && aoiArea != null && (
                    <span className="text-[10px] text-fg-secondary shrink-0">
                        {formatKm2(aoiArea)}
                    </span>
                )}
                {!hasAoi && (
                    <span className="text-[10px] text-fg-secondary shrink-0">
                        current map view
                    </span>
                )}
                <ChevronDown
                    size={10}
                    className={`shrink-0 opacity-60 transition ${open ? "rotate-180" : ""}`}
                />
            </button>

            {/* Fly-to button — only visible when an AOI is selected */}
            {hasAoi && activeAoi?.geom && (
                <button
                    onClick={flyToAoi}
                    title="Fly to AOI"
                    className="absolute right-5 top-[2.6rem] -translate-y-1/2 px-1 py-0.5 rounded
                     text-accent-action/70 hover:text-accent-action hover:bg-accent-action/10 transition"
                >
                    <Crosshair size={11} />
                </button>
            )}

            {open && (
                <div
                    className="absolute left-3 right-3 mt-1 z-20
                     rounded-md border border-slate-700 bg-bg-soft shadow-lg
                     max-h-64 overflow-y-auto"
                >
                    <ScopeOption
                        icon={<Eye size={11} />}
                        label="Viewport"
                        meta="current map view"
                        onClick={pickViewport}
                        active={!hasAoi}
                    />
                    {projectAois.length === 0 ? (
                        <div className="px-3 py-2 text-[10px] text-fg-secondary italic border-t border-slate-800">
                            No AOIs in this project.
                        </div>
                    ) : (
                        <div className="border-t border-slate-800">
                            {projectAois.map((aoi) => (
                                <ScopeOption
                                    key={aoi.id}
                                    icon={<MapPin size={11} />}
                                    label={aoi.name}
                                    meta={
                                        aoi.geom
                                            ? formatKm2(aoiAreaKm2(aoi.geom)) ?? ""
                                            : "no boundary"
                                    }
                                    onClick={() => pickAoi(aoi.id)}
                                    active={activeAoi?.id === aoi.id}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!hasAoi && !open && (
                <p className="mt-1.5 text-[10px] text-fg-secondary leading-relaxed">
                    No AOI selected — actions will use the current map viewport.
                </p>
            )}
        </div>
    );
}

function ScopeOption({ icon, label, meta, onClick, active }) {
    return (
        <button
            onClick={onClick}
            className={[
                "w-full flex items-center gap-2 px-3 py-2 text-xs text-left",
                "hover:bg-bg transition",
                active ? "text-accent-action" : "text-slate-300",
            ].join(" ")}
        >
            <span className="shrink-0 opacity-80">{icon}</span>
            <span className="flex-1 truncate font-medium">{label}</span>
            {meta && <span className="text-[10px] text-fg-secondary shrink-0">{meta}</span>}
        </button>
    );
}